/** Bandeau contenu : Aurélia Factures + recherche globale. */
import { useEffect, useRef, useState, type FormEvent, type KeyboardEvent } from "react";
import { facturxFetch } from "../api/client";
import { useCompany } from "../company/CompanyProvider";
import type { SearchHit, SearchOpen } from "../features/search/types";
import "../features/search/search.css";
import { FacturxGuideModal } from "../features/guide/FacturxGuideModal";
import { SearchClear } from "./SearchClear";

type AppHeaderProps = {
  onOpen?: (hit: SearchOpen) => void;
};

export function AppHeader({ onOpen }: AppHeaderProps) {
  const { company } = useCompany();
  const [q, setQ] = useState("");
  const [hits, setHits] = useState<SearchHit[]>([]);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const [busy, setBusy] = useState(false);
  const boxRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const query = q.trim();
    if (!company || query.length < 2) {
      setHits([]);
      setBusy(false);
      return;
    }
    let stop = false;
    setBusy(true);
    const t = window.setTimeout(() => {
      void facturxFetch<{ hits: SearchHit[] }>(
        `search.php?company_id=${company.id}&q=${encodeURIComponent(query)}`,
      )
        .then((data) => {
          if (stop) return;
          setHits(data.hits || []);
          setActive(0);
          setOpen(true);
        })
        .catch(() => {
          if (!stop) setHits([]);
        })
        .finally(() => {
          if (!stop) setBusy(false);
        });
    }, 180);
    return () => {
      stop = true;
      window.clearTimeout(t);
    };
  }, [q, company]);

  useEffect(() => {
    function onDoc(e: MouseEvent) {
      if (!boxRef.current?.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  function pick(hit: SearchHit) {
    onOpen?.({ kind: hit.kind, id: hit.id });
    setQ("");
    setHits([]);
    setOpen(false);
  }

  function onKey(e: KeyboardEvent<HTMLInputElement>) {
    if (!open && (e.key === "ArrowDown" || e.key === "Enter") && hits.length) {
      setOpen(true);
      return;
    }
    if (e.key === "Escape") {
      setOpen(false);
      return;
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActive((n) => Math.min(hits.length - 1, n + 1));
    }
    if (e.key === "ArrowUp") {
      e.preventDefault();
      setActive((n) => Math.max(0, n - 1));
    }
    if (e.key === "Enter" && hits[active]) {
      e.preventDefault();
      pick(hits[active]);
    }
  }

  function submit(e: FormEvent) {
    e.preventDefault();
    if (hits[active]) pick(hits[active]);
  }

  const showPanel = open && q.trim().length >= 2;

  return (
    <header className="fx-top">
      <div className="fx-top__brand">
        <strong>Aurélia Factures</strong>
      </div>
      <div className="fx-top-search" ref={boxRef}>
        <form className="fx-top__search" onSubmit={submit} role="search">
          <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden>
            <circle cx="11" cy="11" r="7" />
            <path d="M20 20l-3.2-3.2" />
          </svg>
          <input
            value={q}
            onChange={(e) => {
              setQ(e.target.value);
              setOpen(true);
            }}
            onFocus={() => {
              if (hits.length) setOpen(true);
            }}
            onKeyDown={onKey}
            placeholder="Trouvez un contact, un client, une facture…"
            aria-label="Recherche"
            aria-autocomplete="list"
            aria-expanded={showPanel}
            autoComplete="off"
          />
          <SearchClear
            value={q}
            onClear={() => {
              setQ("");
              setHits([]);
              setOpen(false);
            }}
          />
        </form>
        {showPanel ? (
          <div className="fx-top-search__panel" role="listbox">
            {busy && hits.length === 0 ? <p className="fx-top-search__empty">Recherche…</p> : null}
            {!busy && hits.length === 0 ? <p className="fx-top-search__empty">Aucun résultat.</p> : null}
            {hits.map((hit, i) => (
              <button
                key={`${hit.kind}-${hit.id}`}
                type="button"
                role="option"
                aria-selected={i === active}
                className={`fx-top-search__hit${i === active ? " is-on" : ""}`}
                onMouseEnter={() => setActive(i)}
                onClick={() => pick(hit)}
              >
                <span className="fx-top-search__title">{hit.title}</span>
                <span className="fx-top-search__sub">{hit.subtitle}</span>
              </button>
            ))}
          </div>
        ) : null}
      </div>
      <div className="fx-top__tools">
        <FacturxGuideModal />
      </div>
    </header>
  );
}
